import { AttractionTypesList } from '@/components/attraction-types';
import { attractionTypeCrudConfig, type AttractionType } from '@/config/objects/attraction-types';
import { type Attraction } from '@/config/objects/attractions';
import { useApiClient, type CrudConfig } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

export function AttractionTypesTab() {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApiClient();

  const { data: attractionData } = useQuery<{ success: boolean; data: Attraction }>({
    queryKey: ['Attraction', id],
    queryFn: () => apiClient.get(`/attractions/${id}`),
    enabled: !!id,
  });

  const attraction = attractionData?.data;

  const attractionTypesConfig: CrudConfig<AttractionType> = useMemo(
    () => ({
      ...attractionTypeCrudConfig,
      entityName: 'Attraction Type',
      entityNamePlural: 'Attraction Types',
      endpoints: {
        ...attractionTypeCrudConfig.endpoints,
        list: `/attractions/${id}/attraction-types`,
        delete: (attractionTypeId: string) =>
          `/attractions/${id}/attraction-types/${attractionTypeId}`,
      },
      listView: {
        ...attractionTypeCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/attraction-types',
            where: attraction
              ? {
                  attractions: {
                    none: {
                      attractionId: attraction.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (attractionType: AttractionType) => {
              await apiClient.post(`/attractions/${id}/attraction-types`, {
                attractionTypeId: attractionType.id,
              });
              toast.success('Attraction type added to attraction');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (attractionType: AttractionType) => {
              await apiClient.delete(`/attractions/${id}/attraction-types/${attractionType.id}`);
              toast.success('Attraction type removed from attraction');
            },
          },
        },
      },
    }),
    [attraction, id, apiClient],
  );

  if (!id) return null;

  return (
    <AttractionTypesList config={attractionTypesConfig} className="p-0" baseUrl="/attraction-types" />
  );
}
